import React, { useState, useMemo } from 'react';
import { Box, Typography, Alert, Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { alpha, lighten, Theme, useTheme } from '@mui/material/styles';
import type { HarborOnboardingVersion } from './onboarding';
import { DOWNLOAD_EXTENSION_TASK_ID, downloadExtensionOnboardingTask } from './extensionInstallOnboarding';
import { LINKEDIN_SOURCING_TASK_ID, linkedinSourcingOnboardingTask } from './linkedinSourcingOnboarding';

const TASK_IDS_101 = {
  ACCOUNT_SETTINGS: 'account_settings',
  DOWNLOAD_EXTENSION: DOWNLOAD_EXTENSION_TASK_ID,
  CREATING_JOB_FROM_URL: 'creating_job_from_url',
  SOURCING_CANDIDATE_FROM_LINKEDIN: LINKEDIN_SOURCING_TASK_ID,
  CHANGE_CANDIDATE_STATUS: 'change_candidate_status',
} as const;

const TASK_IDS_102 = {
  CONFIGURE_CANDIDATE_EMAIL: 'configure_candidate_email',
  ADD_SENDER_ACCOUNT: 'add_sender_account',
  SEND_SINGLE_EMAIL: 'send_single_email',
  CREATE_SEQUENCE: 'create_sequence',
  ADD_CANDIDATE_TO_SEQUENCE: 'add_candidate_to_sequence',
  START_SEQUENCE: 'start_sequence',
} as const;

const TASK_IDS_103 = {
  CONNECT_CALENDAR: 'connect_calendar',
  SHARE_BOOKING_LINK: 'share_booking_link',
  SEND_OBSERVER_LINK: 'send_observer_link',
} as const;

const ALL_TASK_IDS_101 = Object.values(TASK_IDS_101);
const ALL_TASK_IDS_102 = Object.values(TASK_IDS_102);
const ALL_TASK_IDS_103 = Object.values(TASK_IDS_103);

const ONBOARDING_VERSION_BY_TASK_ID: Record<string, HarborOnboardingVersion> = {
  ...Object.fromEntries(ALL_TASK_IDS_101.map(taskId => [taskId, 'harbor101' as const])),
  ...Object.fromEntries(ALL_TASK_IDS_102.map(taskId => [taskId, 'harbor102' as const])),
  ...Object.fromEntries(ALL_TASK_IDS_103.map(taskId => [taskId, 'harbor103' as const])),
};

/** Resolve API version from task ids so completion is saved to the correct part bucket. */
export const resolveOnboardingVersionForTaskIds = (
  taskIds: readonly string[],
  fallback: HarborOnboardingVersion,
): HarborOnboardingVersion => {
  const versions = new Set(
    taskIds
      .map(taskId => ONBOARDING_VERSION_BY_TASK_ID[taskId])
      .filter((version): version is HarborOnboardingVersion => Boolean(version)),
  );

  if (versions.size === 1) {
    return [...versions][0];
  }

  return fallback;
};

/** Matches MuiButton root sizing in lib/theme.ts — used only in onboarding UI. */
const onboardingControlTypography = {
  fontSize: '0.8125rem',
  lineHeight: '1.4rem',
  fontWeight: 500,
} as const;

/** Shared sizing for onboarding buttons in tour panels, popups, and guide modals. */
export const onboardingActionButtonSx = {
  ...onboardingControlTypography,
  minHeight: '28px',
  padding: '3px 10px',
  borderRadius: '4px',
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
} as const;

export const onboardingStepTextSx = {
  color: 'text.primary',
  ...onboardingControlTypography,
  '& a': {
    ...onboardingControlTypography,
  },
} as const;

// (removed getCommonStyles in favour of simple in-component style objects)

type TextGuideStep = {
  text: React.ReactNode;
  imagePaths?: string[];
};

// Image Zoom Modal Component
const ImageZoomModal = ({
  open,
  onClose,
  imageSrc,
  alt,
}: {
  open: boolean;
  onClose: () => void;
  imageSrc: string;
  alt: string;
}) => {
  const theme = useTheme();
  const zoomDialogPaperSx = {
    backgroundColor: alpha(theme.palette.common.black, 0.9),
    boxShadow: 'none',
    maxWidth: '90vw',
    maxHeight: '90vh',
    margin: '5vh auto',
    width: 'fit-content',
    height: 'fit-content',
  } as const;
  const zoomCloseButtonSx = {
    position: 'absolute',
    top: 8,
    right: 8,
    color: theme.palette.common.white,
    backgroundColor: alpha(theme.palette.common.black, 0.5),
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.black, 0.7),
    },
    zIndex: 1,
  } as const;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: zoomDialogPaperSx,
      }}
    >
      <DialogContent
        sx={{
          padding: 0,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          position: 'relative',
          overflow: 'auto',
          maxWidth: '90vw',
          maxHeight: '90vh',
        }}
      >
        <IconButton onClick={onClose} sx={zoomCloseButtonSx}>
          <CloseIcon />
        </IconButton>
        <img
          src={imageSrc}
          alt={alt}
          style={{
            display: 'block',
            width: 'auto',
            height: 'auto',
            objectFit: 'none',
            minWidth: 'auto',
            minHeight: 'auto',
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export const CommonContent = ({
  steps,
  version,
  alert,
}: {
  alert?: React.ReactNode;
  steps: TextGuideStep[];
  version: '101' | '102' | '103';
}) => {
  const [zoomImage, setZoomImage] = useState<{ src: string; alt: string } | null>(null);
  const theme = useTheme();
  const onboardingStyles = useMemo(() => getCommonStyles(theme), [theme]);
  const commonStyles = useMemo(
    () => ({
      stepContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 1.5,
        p: 2,
        bgcolor: 'action.hover',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
      },
      stepNumberBadge: {
        minWidth: 28,
        height: 28,
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.common.white,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.875rem',
        fontWeight: 600,
        flexShrink: 0,
        mt: 0.25,
      },
      imageCard: {
        mt: 1,
        borderRadius: 1,
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        position: 'relative' as const,
        cursor: 'pointer',
        '&:hover .zoom-overlay': { opacity: 1 },
      },
      zoomOverlay: {
        position: 'absolute' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: alpha(theme.palette.common.black, 0.3),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0,
        transition: 'opacity 0.2s',
      },
      zoomIcon: {
        color: theme.palette.common.white,
        fontSize: '2rem',
        filter: `drop-shadow(0 2px 4px ${alpha(theme.palette.common.black, 0.5)})`,
      },
    }),
    [theme],
  );

  const handleImageClick = (imagePath: string, stepIndex: number, imageIndex: number) => {
    setZoomImage({
      src: `/guide/${version}/${imagePath}`,
      alt: `Step ${stepIndex + 1} guide ${imageIndex + 1}`,
    });
  };

  return (
    <Box>
      {alert && <Alert severity="info">{alert}</Alert>}
      <Typography component="div" variant="body2" sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {steps.map((step, index) => (
            <Box key={index} sx={commonStyles.stepContainer}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Box sx={commonStyles.stepNumberBadge}>{index + 1}</Box>
                <Typography variant="body1" sx={onboardingStyles.stepText}>
                  {step.text}
                </Typography>
              </Box>

              {step.imagePaths &&
                step.imagePaths.map((imagePath, imageIndex) => (
                  <Box
                    key={imageIndex}
                    sx={commonStyles.imageCard}
                    onClick={() => handleImageClick(imagePath, index, imageIndex)}
                  >
                    <img
                      src={`/guide/${version}/${imagePath}`}
                      alt={`Step ${index + 1} guide ${imageIndex + 1}`}
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                      }}
                    />
                    <Box className="zoom-overlay" sx={commonStyles.zoomOverlay}>
                      <ZoomInIcon sx={commonStyles.zoomIcon} />
                    </Box>
                  </Box>
                ))}
            </Box>
          ))}
        </Box>
      </Typography>

      {zoomImage && (
        <ImageZoomModal
          open={!!zoomImage}
          onClose={() => setZoomImage(null)}
          imageSrc={zoomImage.src}
          alt={zoomImage.alt}
        />
      )}
    </Box>
  );
};

const allTasks101 = [
  {
    id: TASK_IDS_101.ACCOUNT_SETTINGS,
    title: 'Add Agency Info',
    completed: false,
  },
  downloadExtensionOnboardingTask,
  {
    id: TASK_IDS_101.CREATING_JOB_FROM_URL,
    title: 'Create Job from URL',
    completed: false,
  },
  linkedinSourcingOnboardingTask,
  {
    id: TASK_IDS_101.CHANGE_CANDIDATE_STATUS,
    title: ' Change status for Candidate',
    completed: false,
  },
];

const allTasks102 = [
  {
    id: TASK_IDS_102.CONFIGURE_CANDIDATE_EMAIL,
    title: 'Add email to Candidate',
    completed: false,
  },
  {
    id: TASK_IDS_102.ADD_SENDER_ACCOUNT,
    title: 'Add Sender Account',
    completed: false,
  },
  {
    id: TASK_IDS_102.SEND_SINGLE_EMAIL,
    title: 'Send Email to Candidate',
    completed: false,
  },
  {
    id: TASK_IDS_102.CREATE_SEQUENCE,
    title: 'Create Sequence',
    completed: false,
  },
  {
    id: TASK_IDS_102.ADD_CANDIDATE_TO_SEQUENCE,
    title: 'Add Candidate to Sequence',
    completed: false,
  },
  {
    id: TASK_IDS_102.START_SEQUENCE,
    title: 'Start Sequence',
    completed: false,
  },
];

const allTasks103 = [
  {
    id: TASK_IDS_103.CONNECT_CALENDAR,
    title: 'Connect Calendar',
    completed: false,
  },
  {
    id: TASK_IDS_103.SHARE_BOOKING_LINK,
    title: 'Share Booking Link with Candidate',
    completed: false,
  },
  {
    id: TASK_IDS_103.SEND_OBSERVER_LINK,
    title: 'Share Public Link for Job',
    completed: false,
  },
];

export const getVersionInfo = (version: 'harbor101' | 'harbor102' | 'harbor103') => {
  const versionMap = {
    harbor101: {
      title: 'Onboarding - Part 1',
      description: 'Complete these tasks in order to get the most out of Harbor:',
      nextVersion: 'harbor102',
      nextVersionTitle: 'Onboarding - Part 2',
      skipButtonText: 'Go to Onboarding - Part 2',
      congratulationsTitle: "You've completed Onboarding - Part 1!",
      congratulationsMessage:
        'Great job completing the basics. Ready to dive deeper? Onboarding - Part 2 will teach you advanced workflow features like sending emails and creating sequences.',
      startButtonText: 'Start Onboarding - Part 2',
      taskList: allTasks101,
      allTaskIds: ALL_TASK_IDS_101,
      isAdvanced: false,
      isClientCollaboration: false,
    },
    harbor102: {
      title: 'Onboarding - Part 2',
      description:
        'Set up outreach in Harbor: configure candidate emails, Add Sender Account, then email and sequences. Complete each task in order:',
      nextVersion: 'harbor103',
      nextVersionTitle: 'Onboarding - Part 3',
      skipButtonText: 'Go to Onboarding - Part 3',
      congratulationsTitle: "You've completed Onboarding - Part 2!",
      congratulationsMessage:
        "Excellent work! You've mastered outreach features like Sender Accounts, email, and sequences. Onboarding - Part 3 will teach you how to share job details using Public Link.",
      startButtonText: 'Start Onboarding - Part 3',
      taskList: allTasks102,
      allTaskIds: ALL_TASK_IDS_102,
      isAdvanced: true,
      isClientCollaboration: false,
    },
    harbor103: {
      title: 'Onboarding - Part 3',
      description: 'Connect your calendar, then share Job details with anyone using Public Link:',
      nextVersion: null,
      nextVersionTitle: null,
      skipButtonText: '',
      congratulationsTitle: "You've completed Onboarding - Part 3!",
      congratulationsMessage: 'Congratulations! You have mastered all Harbor features.',
      startButtonText: '',
      taskList: allTasks103,
      allTaskIds: ALL_TASK_IDS_103,
      isAdvanced: true,
      isClientCollaboration: true,
    },
  };
  return versionMap[version];
};

export const isVersionCompleted = (version: 'harbor101' | 'harbor102' | 'harbor103') => {
  return version === 'harbor103';
};

export const shouldShowVersion = (version: 'harbor101' | 'harbor102' | 'harbor103', isCompleted: boolean) => {
  if (version === 'harbor101') {
    return !isCompleted;
  }
  if (version === 'harbor102') {
    // Part 2 can only be shown if Part 1 is completed (indicated by version being harbor102)
    return !isCompleted;
  }
  if (version === 'harbor103') {
    // Part 3 can only be shown if Part 1 and Part 2 are completed (indicated by version being harbor103)
    return !isCompleted;
  }
  return false;
};

export const getCommonStyles = (theme: Theme) => ({
  fabButton: {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[900],
    boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.2)}`,
    transition: 'transform 0.3s, box-shadow 0.3s',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: `0 6px 25px ${alpha(theme.palette.common.black, 0.25)}`,
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[900],
    },
    width: { xs: 48, sm: 56 },
    height: { xs: 48, sm: 56 },
  },
  badgeStyle: {
    position: 'absolute' as const,
    top: -8,
    right: -8,
    bgcolor: theme.palette.error.main,
    color: theme.palette.getContrastText(theme.palette.error.main),
    borderRadius: '50%',
    width: 26,
    height: 26,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '0.75rem',
    boxShadow: `0 2px 5px ${alpha(theme.palette.common.black, 0.3)}`,
    border: `2px solid ${theme.palette.common.white}`,
    userSelect: 'none',
    zIndex: 1051,
  },
  // Harbor theme: primary.main (#1e3a8a) is the app accent; secondary.main is near-black. Used for all onboarding parts.
  gradientHeader: {
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    px: { xs: 2, sm: 2.5 },
    pt: { xs: 2, sm: 2.5 },
    pb: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'stretch',
    flexShrink: 0,
  },
  /** Contained actions in the popup, tour panel, and guide modals. */
  actionButton: onboardingActionButtonSx,
  /** Guide step copy — same weight as action buttons. */
  stepText: onboardingStepTextSx,
});

export const getOnboardingCardSurfaceStyles = (theme: Theme) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    bgcolor: 'background.paper' as const,
    border: isDark ? `1px solid ${alpha(theme.palette.common.white, 0.16)}` : `1px solid ${theme.palette.divider}`,
    borderRadius: { xs: '12px', sm: '16px' } as const,
    boxShadow: isDark
      ? `0 0 0 1px ${alpha(theme.palette.common.white, 0.06)}, 0 24px 48px ${alpha(theme.palette.common.black, 0.55)}, 0 12px 24px ${alpha(theme.palette.common.black, 0.4)}`
      : theme.shadows[12],
    overflow: 'hidden' as const,
  };
};

export const getUiStyles = (theme: Theme) => ({
  paper: {
    ...getOnboardingCardSurfaceStyles(theme),
    position: 'fixed',
    bottom: { xs: '80px', sm: '90px' },
    width: {
      xs: 'calc(100% - 32px)',
      sm: '360px',
      md: '400px',
    },
    maxWidth: '90vw',
    maxHeight: {
      xs: 'calc(100vh - 140px)',
      sm: '80vh',
    },
    display: 'flex',
    flexDirection: 'column' as const,
    zIndex: 1049,
    left: { xs: '16px', sm: 'auto' },
    right: { xs: 'auto', sm: '20px' },
    mx: { xs: 'auto', sm: 0 },
  },
});

export const getTaskVisuals = (theme: Theme, isCompleted: boolean, isEnabled: boolean, isDisabled: boolean) => {
  const isLight = theme.palette.mode === 'light';
  const { primary, action, text, divider, background } = theme.palette;

  if (isCompleted) {
    if (isLight) {
      return {
        bg: background.paper,
        border: alpha(theme.palette.common.black, 0.12),
        text: alpha(text.primary, 0.82),
        icon: theme.palette.success.main,
      };
    }
    return {
      bg: alpha(theme.palette.common.white, 0.1),
      border: alpha(theme.palette.common.white, 0.2),
      text: alpha(text.primary, 0.95),
      icon: theme.palette.success.main,
    };
  }
  if (isEnabled) {
    if (isLight) {
      return {
        bg: alpha(primary.main, 0.08),
        border: alpha(primary.main, 0.22),
        text: text.primary,
        icon: primary.main,
      };
    }
    return {
      bg: alpha(primary.main, 0.35),
      border: alpha(primary.main, 0.55),
      text: text.primary,
      icon: lighten(primary.main, 0.52),
    };
  }
  if (isDisabled) {
    return {
      bg: action.hover,
      border: divider,
      text: text.disabled,
      icon: text.disabled,
    };
  }
  return {
    bg: background.paper,
    border: divider,
    text: text.primary,
    icon: text.secondary,
  };
};
